import { cloneElement } from "react";
import type { ReactElement, ReactNode } from "react";
import type { ListItemProps } from "@progress/kendo-react-dropdowns";

/**
 * aria-selected on each Kendo MultiSelect option is correct (paired with
 * useMultiSelectListboxMultiselectable's aria-multiselectable="true" on the
 * popup listbox), but some screen readers stay silent, or use a non-speech
 * tone, on the positive "selected" case and only ever speak the negative
 * "not selected" one - confirmed by manual screen reader testing across
 * several of these fields. Appending screen-reader-only text guarantees
 * "selected" gets spoken regardless of the AT's own convention for
 * aria-selected="true".
 *
 * Pass directly as a MultiSelect's `itemRender` prop.
 */
export function multiSelectSelectedItemRender(
  li: ReactElement<HTMLLIElement>,
  itemProps: ListItemProps
): ReactNode {
  if (!itemProps.selected) return li;
  // Kendo types `li` as ReactElement<HTMLLIElement>, using the DOM element
  // interface as the props generic instead of the actual props type, so
  // `li.props.children` resolves to the DOM `HTMLCollection` type rather
  // than React's `ReactNode` - cast to the real shape.
  const { children } = li.props as { children?: ReactNode };
  return cloneElement(
    li,
    undefined,
    children,
    <span key="selected-suffix" className="visually-hidden">
      , selected
    </span>
  );
}
